import apiClient from "./client";
import type { RunForestNode } from "./config-migration";
import type { OrgStructureResponse } from "./configuration-server";
import type {
  OrgNameStatus,
  OrgNameSource,
  OrgRelationStatus,
  OrgConformityStatus,
  UnitOrigin,
  ScopeVerdict,
} from "@/lib/org-structure-tree";
import type { components } from "@/types/api.generated";
export type OrgRunKey = components["schemas"]["OrgExecutionKey"];
type ContextResponse = components["schemas"]["OrgUnitContextResponse"];
export type OrgUnitContext = Omit<
  ContextResponse,
  "row" | "unit" | "ancestors" | "snapshot"
> & {
  snapshot: OrgStructureResponse;
  row: NonNullable<ContextResponse["row"]>;
  unit: RunForestNode;
  ancestors: RunForestNode[];
};
function enumValue<T extends string>(
  value: string,
  allowed: readonly T[],
  fallback: T,
): T {
  return allowed.find((item) => item === value) ?? fallback;
}
function snapshot(data: ContextResponse["snapshot"]): OrgStructureResponse {
  return {
    ...data,
    artifact_id: data.artifact_id ?? null,
    execution_id: data.execution_id ?? null,
    source_system:
      data.source_system === "ecc" || data.source_system === "s4hana"
        ? data.source_system
        : null,
    levels: data.levels.map((level) => ({
      ...level,
      parent_level_code: level.parent_level_code ?? null,
      source_table: level.source_table ?? null,
      naming_rule: level.naming_rule ?? null,
      nodes: level.nodes.map((node) => ({
        ...node,
        name: node.name ?? null,
        parent_id: node.parent_id ?? null,
        name_status: enumValue<OrgNameStatus>(
          node.name_status,
          ["extracted", "not_extracted"],
          "not_extracted",
        ),
        name_source: enumValue<OrgNameSource>(
          node.name_source,
          [
            "direct_field",
            "text_table",
            "not_available",
            "consultant_override",
          ],
          "not_available",
        ),
        relation_status: enumValue<OrgRelationStatus>(
          node.relation_status,
          [
            "assigned",
            "no_parent",
            "unknown_parent",
            "many_to_many",
            "not_applicable",
          ],
          "not_applicable",
        ),
        origin: enumValue<UnitOrigin>(node.origin, ["sap", "manual"], "sap"),
        scope_verdict: enumValue<ScopeVerdict>(
          node.scope_verdict,
          ["pending", "approved", "out_of_scope"],
          "pending",
        ),
        conformity: {
          ...node.conformity,
          status: enumValue<OrgConformityStatus>(
            node.conformity.status,
            ["conforming", "non_conforming", "not_evaluable"],
            "not_evaluable",
          ),
        },
      })),
    })),
  };
}
const base = (projectId: string) =>
  `/api/projects/${projectId}/configuration-server/management`;
export async function getOrgRunnableKeys(
  projectId: string,
  artifactId?: string,
): Promise<components["schemas"]["OrgExecutionKeysResponse"]> {
  const res = await apiClient.get<
    components["schemas"]["OrgExecutionKeysResponse"]
  >(`${base(projectId)}/org-runnable-keys`, {
    params: { artifact_id: artifactId },
  });
  return res.data;
}
export async function getOrgStatuses(
  projectId: string,
  keys: components["schemas"]["OrgStatusKey"][],
  artifactId?: string,
): Promise<components["schemas"]["OrgExecutionKeysResponse"]> {
  const res = await apiClient.post<
    components["schemas"]["OrgExecutionKeysResponse"]
  >(
    `${base(projectId)}/org-status`,
    { keys },
    { params: { artifact_id: artifactId } },
  );
  return res.data;
}
function runNode(node: components["schemas"]["OrgRunNode"]): RunForestNode {
  return {
    ...node,
    parent_key: node.parent_key ?? null,
    parent_type: node.parent_type ?? null,
  };
}
export async function getOrgUnitContext(
  projectId: string,
  key: string,
  artifactId?: string,
): Promise<OrgUnitContext> {
  const split = key.indexOf("|");
  const res = await apiClient.get<ContextResponse>(
    `${base(projectId)}/org-unit-context`,
    {
      params: {
        item_type: key.slice(0, split),
        item_key: key.slice(split + 1),
        artifact_id: artifactId,
      },
    },
  );
  const data = res.data;
  if (!data.row || !data.unit)
    throw new Error("Unidade organizacional não encontrada.");
  return {
    ...data,
    snapshot: snapshot(data.snapshot),
    row: data.row,
    unit: runNode(data.unit),
    ancestors: data.ancestors.map(runNode),
  };
}
export async function getOrgMetadata(
  projectId: string,
  artifactId?: string,
): Promise<{ snapshot: OrgStructureResponse }> {
  const res = await apiClient.get<ContextResponse>(
    `${base(projectId)}/org-unit-context`,
    { params: { artifact_id: artifactId } },
  );
  return { snapshot: snapshot(res.data.snapshot) };
}
